import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Header from "./Header";
import Sidebar from "./Sidebar";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const isEditorPage = location.pathname.startsWith("/editor");
  const isHomePage = location.pathname === "/home";
  const isDiscoverPage = location.pathname === "/discover";
  
  // Show sidebar on documents, templates, tags, and workspaces pages
  const isDocumentsPage = location.pathname === "/documents" || location.pathname.startsWith("/documents");
  const isTemplatesPage = location.pathname === "/templates";
  const isTagsPage = location.pathname === "/tags";
  const isWorkspacesPage = location.pathname.startsWith("/workspaces");
  
  const showSidebar = isDocumentsPage || isTemplatesPage || isTagsPage || isWorkspacesPage;

  // Close sidebar when route changes on mobile
  useEffect(() => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && sidebarOpen) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [sidebarOpen]);

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <Header 
        className="relative z-40" 
        onSidebarToggle={showSidebar ? () => setSidebarOpen(!sidebarOpen) : undefined}
        sidebarOpen={sidebarOpen}
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - show on non-editor, non-home pages */}
        {showSidebar && (
          <Sidebar 
            isOpen={sidebarOpen} 
            onClose={() => setSidebarOpen(false)} 
          />
        )}

        {/* Main Content */}
        <main
          className={`flex-1 ${
            isEditorPage ? "overflow-hidden" : "overflow-y-auto"
          }`}
        >
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
